import { useEffect, useRef } from 'react';

import { useChartContext } from '../../../components/ChartContext';
import { useTooltipContext } from '../Tooltip/TooltipContext';

/**
 * Component that invokes the onDataPointHover callback when the hovered data point changes.
 * Connects the tooltip's activeXValue state to the chart-level callback.
 *
 * Uses a ref to track the previous activeXValue to prevent infinite loops when
 * onDataPointHover callback reference changes but the actual value hasn't changed.
 */
export function HoverTrackingHandler() {
  const { chartData, xDataKey, onDataPointHover } = useChartContext();
  const { activeXValue } = useTooltipContext();

  // Track previous activeXValue to prevent calling onDataPointHover with the same value
  const prevActiveXValueRef = useRef<number | string | null>(null);
  const prevDataPointRef = useRef<unknown>(null);

  useEffect(() => {
    if (!onDataPointHover) {
      return;
    }

    // Only call onDataPointHover if activeXValue actually changed
    if (activeXValue === prevActiveXValueRef.current) {
      return;
    }

    // Update ref before calling callback to prevent re-entrancy
    prevActiveXValueRef.current = activeXValue;

    if (activeXValue === null) {
      prevDataPointRef.current = null;
      onDataPointHover(null);
      return;
    }

    // Find the data point matching the active X value
    const activeDataPoint = chartData?.find((point) => point[xDataKey] === activeXValue) ?? null;

    // Only call if data point actually changed (by reference comparison)
    if (activeDataPoint === prevDataPointRef.current) {
      return;
    }

    prevDataPointRef.current = activeDataPoint;
    onDataPointHover(activeDataPoint);
  }, [activeXValue, chartData, xDataKey, onDataPointHover]);

  return null;
}
